import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { BrowserRouter, Routes, Route, Link, NavLink, useNavigate, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import {
  LayoutDashboard, FolderKanban, FileCheck2, ScrollText, Image as ImageIcon,
  Sparkles, BookOpen, LogOut, Search, Send, Upload, ShieldCheck, AlertTriangle,
  CheckCircle2, ChevronRight, Building2, Hash, BarChart3, MessageSquareText, Loader2, X,
  Inbox, Link as LinkIcon, Menu, LogIn, FileText
} from "lucide-react";

// --- Extracted page/component modules (refactor 2026-02) ---
import { Login } from "./pages/Login";
import { PhotoChecker } from "./pages/PhotoChecker";
import { IngestInbox } from "./pages/IngestInbox";
import { CETVerifier } from "./pages/CETVerifier";
import { AIAssistant } from "./pages/AIAssistant";
import { KnowledgeBase } from "./pages/KnowledgeBase";
import { WorkflowEPARScope, WorkflowNSWRABenefits } from "./pages/WorkflowEPARScope";
import { WorkflowEWFinalisation } from "./pages/WorkflowEWFinalisation";
import { WorkflowEPARFinalisation } from "./pages/WorkflowEPARFinalisation";
import { SecurityStatement } from "./pages/SecurityStatement";
import { Precedents } from "./pages/Precedents";
import { Showcase } from "./pages/Showcase";
// ------------------------------------------------------------


// --- Refactor pass 3 ---
import { CETSlipBreakdown, DMEvidenceDrawer, DMDecisionsBlock, EWLedgerPanel, EWPhotoStrip, EWDefectPhotos, EWDefectRow, EWFinalisationPanel, IngestJob } from "./components/IngestJobParts";
// --- Refactor pass 2 ---
import { applyLedgerOverrides, LedgerVerdictBtn, LedgerRowOverrideControls, LedgerChecker } from "./pages/LedgerChecker";
import { FileInspectModal, SourceTreeSortableHeader, SourcePackageTree, VerifiedPackageTree } from "./components/PackageTrees";
import { API } from "./lib/api";
import { fmtAUD, fmtMoney } from "./lib/format";
import { useAuth } from "./hooks/useAuth";
import { SLAPill } from "./components/SLAPill";
import { Stat } from "./lib/ui";

/* ---------- Auth context (lightweight) ---------- */
/* useAuth now lives in /hooks/useAuth.js */

/* ---------- Login ---------- */

/* ---------- Layout ---------- */
const NAV = [
  { to:"/", label:"Dashboard", icon: LayoutDashboard, end:true },
  { to:"/inbox", label:"Ingest Inbox", icon: Inbox },
  { kind:"section", label:"Assessment Portals" },
  { to:"/portal/EW%20Finalisation", label:"EW Finalisation", icon: FileCheck2 },
  { to:"/portal/IRW%20Scope%20Approval", label:"IRW Scope Approval", icon: FolderKanban },
  { to:"/portal/IRW%20Finalisation", label:"IRW Finalisation", icon: FileCheck2 },
  { to:"/portal/EPAR%20Scope%20Approval", label:"EPAR Scope Approval", icon: FolderKanban },
  { to:"/portal/EPAR%20Finalisation", label:"EPAR Finalisation", icon: FileCheck2 },
  { kind:"section", label:"Tools" },
  { to:"/tools/ledger-checker", label:"Ledger Checker", icon: ScrollText },
  { to:"/tools/photo-checker", label:"Photo Checker", icon: ImageIcon },
  { to:"/tools/cet-verifier", label:"CET Verifier", icon: BarChart3 },
  { to:"/ai-assistant", label:"DRFA Expert", icon: Sparkles },
  { kind:"section", label:"Reference" },
  { to:"/knowledge-base", label:"Knowledge Base", icon: BookOpen },
  { to:"/precedents", label:"Precedent corpus", icon: ShieldCheck },
];

function Shell({ user, onLogout, children }) {
  const [navOpen, setNavOpen] = useState(false);
  const [workflowsOpen, setWorkflowsOpen] = useState(false);
  const workflowsRef = useRef(null);
  const closeNav = () => setNavOpen(false);
  const isGuest = !user;
  // Close the Workflows dropdown on outside click / Escape.
  useEffect(() => {
    if (!workflowsOpen) return;
    const onDoc = (e) => {
      if (workflowsRef.current && !workflowsRef.current.contains(e.target)) {
        setWorkflowsOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") setWorkflowsOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [workflowsOpen]);
  return (
    <div className="min-h-screen flex" data-testid="app-shell">
      {/* mobile overlay */}
      {navOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={closeNav} />}
      <aside className={`fixed lg:static inset-y-0 left-0 w-64 shrink-0 flex flex-col z-40 transform transition-transform duration-200 ${navOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`} style={{ background:"#002664" }}>
        <div className="h-16 flex items-center gap-3 px-5 border-b border-white/10">
          <div className="w-8 h-8 rounded-md bg-white flex items-center justify-center"><ShieldCheck className="w-5 h-5" style={{color:"#002664"}}/></div>
          <div className="text-white">
            <div className="font-heading font-bold text-[15px] leading-none">RAAgent</div>
            <div className="text-[10px] tracking-[0.16em] uppercase opacity-70 mt-0.5">{isGuest ? "Standalone Tools" : "Assessment Portal"}</div>
          </div>
        </div>
        <nav className="px-3 py-4 space-y-0.5 flex-1 overflow-y-auto">
          {NAV.filter(n => isGuest ? (n.kind === "section" ? n.label === "Tools" : (n.to || "").startsWith("/tools/")) : true).map((n, i) => n.kind === "section" ? (
            <div key={i} className="px-3 pt-4 pb-1 text-[10px] tracking-[0.18em] uppercase text-white/40 font-semibold">{n.label}</div>
          ) : (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={closeNav} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
              className={({isActive}) => `side-link ${isActive ? "active" : ""}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-5 py-4 border-t border-white/10" data-testid="powered-by-raagent">
          <div className="text-[9.5px] tracking-[0.22em] uppercase text-white/40 mb-1">Powered by</div>
          <div className="font-heading font-bold text-white text-lg leading-none tracking-tight">RAAgent</div>
          <div className="text-[10px] text-white/40 mt-1">RHE Project Consulting</div>
        </div>
      </aside>
      <main className="flex-1 flex flex-col min-w-0 w-full">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={()=>setNavOpen(true)} className="lg:hidden p-1.5 rounded hover:bg-slate-100" aria-label="Open navigation" data-testid="mobile-nav-toggle">
              <Menu className="w-5 h-5"/>
            </button>
            <div className="text-[12px] text-slate-500 truncate hidden sm:block">
              <span className="font-mono">RHE</span> · {isGuest ? "Standalone tools (no login required)" : "Assessor workspace"}
              <span className="px-1.5 py-0.5 rounded bg-slate-900 text-white text-[9.5px] tracking-[0.18em] uppercase font-semibold ml-2 hidden md:inline-block">Powered by RAAgent</span>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {isGuest && (
              <NavLink
                to="/showcase"
                className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] tracking-[0.12em] uppercase font-bold bg-[#002664] text-white hover:bg-[#001a4d] border border-[#002664]"
                data-testid="header-live-demo-link"
                title="60-second product overview — no sign-in required"
              >
                ▸ Live demo
              </NavLink>
            )}
            <Link
              to="/security"
              className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 text-[10.5px] tracking-[0.12em] uppercase font-bold border border-emerald-200 hover:bg-emerald-100"
              data-testid="header-security-badge"
              title="Read RAAgent's security & AI transparency statement"
            >
              <ShieldCheck className="w-3 h-3"/> Security &amp; AI
            </Link>
            <div ref={workflowsRef} className="relative hidden sm:block" data-testid="workflows-nav">
              <button
                type="button"
                onClick={() => setWorkflowsOpen(o => !o)}
                aria-expanded={workflowsOpen}
                aria-haspopup="menu"
                className="btn-secondary !py-1.5 !px-3 flex items-center gap-1.5"
                data-testid="workflows-nav-button"
              >
                <BookOpen className="w-3.5 h-3.5"/>
                <span>Workflows</span>
                <span className={`text-[10px] transition-transform ${workflowsOpen ? "rotate-180" : ""}`}>▾</span>
              </button>
              {workflowsOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full mt-1 w-64 rounded-md border border-slate-200 bg-white shadow-lg z-50 py-1"
                  data-testid="workflows-nav-menu"
                >
                  <div className="px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500 border-b border-slate-100">Narrated walkthroughs</div>
                  <NavLink to="/workflow/ew-finalisation" onClick={() => setWorkflowsOpen(false)} className="block px-3 py-2 text-[12px] hover:bg-slate-50" data-testid="workflows-nav-ew-final">EW Finalisation</NavLink>
                  <NavLink to="/workflow/epar-scope" onClick={() => setWorkflowsOpen(false)} className="block px-3 py-2 text-[12px] hover:bg-slate-50" data-testid="workflows-nav-epar-scope">EPAR Scope Approval</NavLink>
                  <NavLink to="/workflow/epar-finalisation" onClick={() => setWorkflowsOpen(false)} className="block px-3 py-2 text-[12px] hover:bg-slate-50 border-b border-slate-100" data-testid="workflows-nav-epar-final">EPAR Finalisation</NavLink>
                  <div className="px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500 border-b border-slate-100">Assessment portals</div>
                  <NavLink to={isGuest ? "/login" : "/portal/IRW%20Scope%20Approval"} onClick={() => setWorkflowsOpen(false)} className="block px-3 py-2 text-[12px] hover:bg-slate-50" data-testid="workflows-nav-irw-scope">IRW Scope Approval{isGuest && <span className="ml-1.5 text-[9px] text-slate-400">(sign in)</span>}</NavLink>
                  <NavLink to={isGuest ? "/login" : "/portal/IRW%20Finalisation"} onClick={() => setWorkflowsOpen(false)} className="block px-3 py-2 text-[12px] hover:bg-slate-50 border-b border-slate-100" data-testid="workflows-nav-irw-final">IRW Finalisation{isGuest && <span className="ml-1.5 text-[9px] text-slate-400">(sign in)</span>}</NavLink>
                  <a href="/api/presentation" target="_blank" rel="noopener" onClick={() => setWorkflowsOpen(false)} className="block px-3 py-2 text-[12px] hover:bg-slate-50 text-blue-700 font-semibold" data-testid="workflows-nav-briefing">▸ Full briefing deck</a>
                </div>
              )}
            </div>
            {isGuest ? (
              <NavLink to="/login" className="btn-secondary !py-1.5 !px-3 flex items-center gap-1.5" data-testid="login-link">
                <LogIn className="w-3.5 h-3.5"/><span className="hidden sm:inline">Sign in</span>
              </NavLink>
            ) : (
              <>
                <div className="text-right hidden sm:block">
                  <div className="text-[13px] font-semibold truncate max-w-[180px]">{user?.name}</div>
                  <div className="text-[11px] text-slate-500 truncate max-w-[200px]">{user?.role}</div>
                </div>
                <div className="w-9 h-9 rounded-full bg-slate-200 overflow-hidden shrink-0">
                  <img src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&q=85&w=200" alt="" className="w-full h-full object-cover" />
                </div>
                <button onClick={onLogout} className="btn-secondary !py-1.5 !px-3 flex items-center gap-1.5" data-testid="logout-button"><LogOut className="w-3.5 h-3.5"/><span className="hidden sm:inline">Sign out</span></button>
              </>
            )}
          </div>
        </header>
        <div className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
}

/* ---------- Status pill ---------- */
function StatusPill({ status }) {
  const map = {
    "Draft":"pill-draft", "In Review":"pill-review", "RFI Issued":"pill-rfi",
    "Approved":"pill-approved", "Partially Approved":"pill-partial", "Ineligible":"pill-ineligible"
  };
  return <span className={`pill ${map[status] || "pill-draft"}`} data-testid={`status-${status?.toLowerCase().replace(/\s+/g,'-')}`}>{status}</span>;
}

/* fmtAUD imported from lib/format */

/* ---------- Dashboard ---------- */
function Dashboard() {
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [subs, setSubs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const loadJobs = useCallback(() => {
    axios.get(`${API}/ingest/jobs`).then(r => setJobs(r.data || [])).catch(()=>setJobs([]));
  }, []);
  const loadMyJobs = useCallback(() => {
    if (!user?.email) { setMyJobs([]); return; }
    axios.get(`${API}/ingest/my-jobs`, { params: { assessor_email: user.email } })
      .then(r => setMyJobs(r.data || []))
      .catch(() => setMyJobs([]));
  }, [user?.email]);
  useEffect(() => {
    axios.get(`${API}/dashboard/metrics`).then(r => setM(r.data));
    axios.get(`${API}/submissions`).then(r => setSubs(r.data.slice(0, 10)));
    loadJobs();
    loadMyJobs();
  }, [loadJobs, loadMyJobs]);
  const openReview = jobs.reduce((acc, j) => acc + (j.needs_review || []).filter(x => !x.resolved).length, 0);
  const cards = [
    { label:"Pipeline value", value: m ? fmtAUD(m.pipeline_value) : "—", sub:"Across all 5 portals", icon: BarChart3 },
    { label:"In review", value: m?.in_review_count ?? "—", sub:"Awaiting assessor action", icon: FileCheck2 },
    { label:"RFI issued", value: m?.rfi_count ?? "—", sub:"Outstanding council response", icon: AlertTriangle },
    { label:"Approved", value: m?.approved_count ?? "—", sub:"Cleared for SmartyGrants sync", icon: CheckCircle2 },
  ];
  return (
    <div className="fade-up" data-testid="dashboard-page">
      <div className="mb-1 label-upper">Assessor Workspace</div>
      <h1 className="font-heading text-3xl font-bold mb-1 tracking-tight">Good day. Here&apos;s your pipeline.</h1>
      <p className="text-sm text-slate-500 mb-8">Submissions assessed in real-time against DRFA Standard Treatment Guide v2.1 and NSW DAG 2021. <span className="inline-flex items-center gap-1 ml-1 px-1.5 py-0.5 rounded bg-slate-900 text-white text-[10px] tracking-[0.16em] uppercase font-semibold align-middle">Powered by RAAgent</span></p>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        {cards.map((c, i) => (
          <div key={i} className="card p-5" data-testid={`metric-${c.label.toLowerCase().replace(/\s+/g,'-')}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="label-upper">{c.label}</div>
              <c.icon className="w-4 h-4 text-slate-400" />
            </div>
            <div className="font-heading text-3xl font-bold tabular">{c.value}</div>
            <div className="text-[12px] text-slate-500 mt-1">{c.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-8">
        <div className="card p-5 xl:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold">Submissions by portal</h3>
            <Link to="/portal/EW%20Finalisation" className="text-xs text-blue-600 hover:underline">View all →</Link>
          </div>
          <div className="space-y-2">
            {m && Object.entries(m.by_portal).map(([k, v]) => {
              const max = Math.max(...Object.values(m.by_portal));
              return (
                <div key={k} className="flex items-center gap-3 text-sm">
                  <div className="w-44 text-slate-700">{k}</div>
                  <div className="flex-1 bg-slate-100 rounded h-2 overflow-hidden">
                    <div className="h-full" style={{ width: `${(v/max)*100}%`, background:"#002664" }} />
                  </div>
                  <div className="w-8 text-right font-mono tabular">{v}</div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="card p-5">
          <h3 className="font-heading text-lg font-semibold mb-4">Status breakdown</h3>
          <div className="space-y-2">
            {m && Object.entries(m.by_status).map(([k,v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <StatusPill status={k} />
                <span className="font-mono tabular text-slate-700">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {myJobs.length > 0 && (
        <div className="card overflow-hidden mb-8 border-blue-200 bg-blue-50/30" data-testid="resume-jobs-panel">
          <div className="flex items-center justify-between px-5 py-4 border-b border-blue-200">
            <div className="flex items-center gap-3">
              <h3 className="font-heading text-lg font-semibold text-blue-900">Resume your jobs</h3>
              <span className="pill pill-review" data-testid="resume-jobs-count">{myJobs.length} in progress</span>
            </div>
            <div className="text-[11px] text-blue-700/80 font-mono">Assigned to {user?.email}</div>
          </div>
          <table className="dense">
            <thead><tr><th>Package</th><th>Portal</th><th>AGRN / Claimant</th><th>SLA</th><th>Last active</th><th></th></tr></thead>
            <tbody>
              {myJobs.map(j => {
                const agrn = j?.form_306?.header?.agrn || j?.pdf_meta?.agrn;
                const claimant = j?.form_306?.header?.claimant || j?.pdf_meta?.claimant;
                const lastActive = j.assessor_last_active_at ? new Date(j.assessor_last_active_at).toLocaleString() : "—";
                return (
                  <tr key={j.id} data-testid={`resume-row-${j.id}`}>
                    <td className="font-mono text-[12px]">{j.top_dir_display || j.top_dir || j.id}</td>
                    <td>{j.portal_type}</td>
                    <td className="font-mono text-[12px]">
                      {agrn || <span className="text-amber-700">Needs sorting</span>}
                      {claimant && <div className="text-[11px] text-slate-500 font-sans">{claimant}</div>}
                    </td>
                    <td><SLAPill sla={j.sla} testid={`resume-sla-${j.id}`}/></td>
                    <td className="text-[11px] text-slate-600">{lastActive}</td>
                    <td>
                      <Link to={`/inbox/${j.id}`} className="inline-flex items-center justify-center px-2.5 py-1 text-[11px] tracking-[0.14em] uppercase font-bold rounded-md bg-blue-600 text-white hover:bg-blue-700" data-testid={`resume-open-${j.id}`}>Resume →</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="card overflow-hidden mb-8" data-testid="needs-review-panel">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <h3 className="font-heading text-lg font-semibold">Needs your sorting</h3>
            {openReview > 0 && <span className="pill pill-rfi" data-testid="needs-review-badge">{openReview} open</span>}
            {openReview === 0 && jobs.length > 0 && <span className="pill pill-approved">All clear</span>}
          </div>
          <Link to="/inbox" className="text-xs text-blue-600 hover:underline" data-testid="view-inbox-link">Open inbox →</Link>
        </div>
        {jobs.length === 0 ? (
          <div className="px-5 py-6 text-sm text-slate-500">No ingested packages yet. Drop a SmartyGrants ZIP or paste a Drive link in any portal to begin.</div>
        ) : (
          <table className="dense">
            <thead><tr><th>Package</th><th>Portal</th><th>AGRN / Claimant</th><th>Items</th><th>Photos</th><th>SLA</th><th>Open issues</th><th></th></tr></thead>
            <tbody>
              {jobs.slice(0, 8).map(j => {
                const open = (j.needs_review || []).filter(x => !x.resolved).length;
                const agrn = j?.form_306?.header?.agrn || j?.pdf_meta?.agrn;
                const claimant = j?.form_306?.header?.claimant || j?.pdf_meta?.claimant;
                return (
                  <tr key={j.id} data-testid={`inbox-row-${j.id}`}>
                    <td className="font-mono text-[12px]">{j.top_dir_display || j.top_dir || j.id}</td>
                    <td>{j.portal_type}</td>
                    <td className="font-mono text-[12px]">
                      {agrn ? (
                        <>
                          {agrn}
                          <div className="text-[11px] text-slate-500 font-sans">{claimant || ""}</div>
                        </>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] tracking-[0.12em] uppercase font-semibold">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Needs sorting
                        </span>
                      )}
                    </td>
                    <td className="font-mono">{j?.form_306?.item_count ?? "—"}</td>
                    <td className="font-mono">{(j?.evidence?.total_damage_photos ?? 0) + (j?.evidence?.total_completion_photos ?? 0)}</td>
                    <td><SLAPill sla={j.sla} compact testid={`inbox-sla-${j.id}`} /></td>
                    <td>{open > 0 ? <span className="pill pill-rfi">{open}</span> : <span className="pill pill-approved">0</span>}</td>
                    <td>
                      <div className="flex flex-col items-stretch gap-1" style={{minWidth: 110}}>
                        <Link to={`/inbox/${j.id}`} className="inline-flex items-center justify-center px-2.5 py-1 text-[11px] tracking-[0.14em] uppercase font-bold rounded-md bg-blue-600 text-white hover:bg-blue-700">Assess →</Link>
                        <button
                          type="button"
                          data-testid={`dashboard-delete-claim-${j.id}`}
                          onClick={async () => {
                            if (!window.confirm(`Delete this claim?\n\n${j.top_dir_display || j.top_dir || j.id}\n\nThis removes the ingest job, its linked submission and all on-disk evidence. This cannot be undone.`)) return;
                            try {
                              await axios.delete(`${API}/ingest/jobs/${j.id}`);
                              loadJobs();
                            } catch (e) {
                              alert("Delete failed: " + (e?.response?.data?.detail || e?.message));
                            }
                          }}
                          className="inline-flex items-center justify-center px-2.5 py-1 text-[10.5px] tracking-[0.14em] uppercase font-semibold rounded-md bg-white text-red-600 border border-red-300 hover:bg-red-50"
                        >Delete claim</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="font-heading text-lg font-semibold">Recent submissions</h3>
          <div className="text-xs text-slate-500">Latest 10 across all portals</div>
        </div>
        <SubmissionsTable rows={subs} />
      </div>
    </div>
  );
}

/* ---------- Reusable submissions table ---------- */
function SubmissionsTable({ rows }) {
  return (
    <div className="overflow-auto">
      <table className="dense" data-testid="submissions-table">
        <thead>
          <tr>
            <th>Claim Ref</th>
            <th>AGRN / Council</th>
            <th className="text-right">Value</th>
            <th className="text-right">DMs</th>
            <th className="text-right">Photos</th>
            <th>Verification progress</th>
            <th>Ingest</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const dp = r.decisions_progress;
            const total = dp?.total || 0;
            const verified = dp ? dp.yes : 0;
            const ingestTone = r.ingest_status === "ready" ? "pill-approved"
              : r.ingest_status === "processing" ? "pill-review"
              : r.ingest_status === "error" ? "pill-ineligible"
              : "pill-draft";
            return (
              <tr key={r.id} data-testid={`row-${r.id}`}>
                <td className="font-mono text-[12.5px]">{r.claim_ref}</td>
                <td className="font-mono">
                  {r.agrn}
                  <div className="text-[11px] text-slate-500 font-sans">{r.council}{r.lga ? ` · ${r.lga}` : ""}</div>
                </td>
                <td className="text-right font-mono tabular">{fmtAUD(r.value)}</td>
                <td className="text-right font-mono">{r.dm_count || "—"}</td>
                <td className="text-right font-mono">{r.photo_count || "—"}</td>
                <td className="text-[11px] font-mono">
                  {dp && total > 0 ? (
                    <div className="flex items-center gap-1.5">
                      <div className="w-20 h-1.5 bg-slate-200 rounded overflow-hidden">
                        <div className="h-full bg-emerald-600" style={{width: `${(verified/total)*100}%`}}/>
                      </div>
                      <span><span className="text-emerald-700">{dp.yes}Y</span> · <span className="text-amber-700">{dp.rfi}R</span> · <span className="text-red-700">{dp.no}N</span> · <span className="text-slate-500">{dp.pending}P</span></span>
                    </div>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td>
                  {r.ingest_status
                    ? <span className={`pill ${ingestTone}`}>{r.ingest_status}</span>
                    : <span className="text-slate-400 text-[11px]">—</span>}
                </td>
                <td><StatusPill status={r.status} /></td>
                <td className="whitespace-nowrap">
                  <Link to={`/submission/${r.id}`} className="text-blue-600 text-xs hover:underline mr-2">Open</Link>
                  {r.ingest_job_id && (
                    <Link to={`/inbox/${r.ingest_job_id}`} className="text-blue-600 text-xs hover:underline">Assess →</Link>
                  )}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (<tr><td colSpan="9" className="text-center py-10 text-slate-500 text-sm">No submissions.</td></tr>)}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Portal page (list per portal type) ---------- */
function PortalPage() {
  const { portal_type } = useParams();
  const decoded = decodeURIComponent(portal_type);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  useEffect(() => {
    const params = { portal_type: decoded };
    if (statusFilter) params.status = statusFilter;
    axios.get(`${API}/submissions`, { params }).then(r => setRows(r.data));
  }, [decoded, statusFilter]);
  return (
    <div className="fade-up" data-testid="portal-page">
      <div className="label-upper mb-1">Assessment Portal</div>
      <h1 className="font-heading text-3xl font-bold mb-1">{decoded}</h1>
      <p className="text-sm text-slate-500 mb-6">{portalBlurb(decoded)}</p>

      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <select className="input max-w-[200px]" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} data-testid="filter-status">
          <option value="">All statuses</option>
          {["In Review","RFI Issued","Approved","Partially Approved","Ineligible","Draft"].map(s=><option key={s}>{s}</option>)}
        </select>
        {decoded === "EPAR Scope Approval" && (
          <Link to="/workflow/epar-scope" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50 text-[12px] font-semibold" data-testid="workflow-link">
            <BookOpen className="w-3.5 h-3.5"/> View workflow
          </Link>
        )}
        {decoded === "EW Finalisation" && (
          <Link to="/workflow/ew-finalisation" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50 text-[12px] font-semibold" data-testid="workflow-link-ew">
            <BookOpen className="w-3.5 h-3.5"/> View workflow
          </Link>
        )}
        {decoded === "EPAR Finalisation" && (
          <Link to="/workflow/epar-finalisation" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50 text-[12px] font-semibold" data-testid="workflow-link-epar-final">
            <BookOpen className="w-3.5 h-3.5"/> View workflow
          </Link>
        )}
        <div className="ml-auto text-xs text-slate-500">{rows.length} submissions</div>
      </div>

      <div className="card overflow-hidden"><SubmissionsTable rows={rows} /></div>
    </div>
  );
}

function portalBlurb(p) {
  return ({
    "EW Finalisation":"Finalise Emergency Works claims — reconcile ledger, evidence and on-cost rates against the EW Eligibility Criteria.",
    "IRW Scope Approval":"Approve scope of Immediate Reconstruction Works against pre-condition certification and the Standard Treatment Guide v2.1.",
    "IRW Finalisation":"Finalise IRW delivery — works substantially complete within 12 months of the event.",
    "EPAR Scope Approval":"Approve EPAR scope and ERC against EPAR Disaster Grant Guidelines.",
    "EPAR Finalisation":"Reconcile EPAR delivery against the approved ERC and pre-disaster function.",
  })[p] || "DRFA assessment portal.";
}

/* ---------- Submission detail ---------- */
function SubmissionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const sub = s;
  const [saving, setSaving] = useState(false);
  const [statusDraft, setStatusDraft] = useState("");
  const [notesDraft, setNotesDraft] = useState("");
  useEffect(() => {
    axios.get(`${API}/submissions/${id}`).then(r => {
      setS(r.data); setStatusDraft(r.data.status); setNotesDraft(r.data.notes||"");
    });
  }, [id]);
  const save = async () => {
    setSaving(true);
    const r = await axios.patch(`${API}/submissions/${id}`, { status: statusDraft, notes: notesDraft });
    setS(r.data); setSaving(false);
  };
  if (!s) return <div className="text-sm text-slate-500">Loading…</div>;
  return (
    <div className="fade-up" data-testid="submission-detail">
      <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
        <Link to="/" className="hover:underline">Dashboard</Link><ChevronRight className="w-3 h-3"/>
        <Link to={`/portal/${encodeURIComponent(s.portal_type)}`} className="hover:underline">{s.portal_type}</Link>
        <ChevronRight className="w-3 h-3"/><span className="font-mono">{s.claim_ref}</span>
      </div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="label-upper mb-1">{s.portal_type}</div>
          <h1 className="font-heading text-3xl font-bold">{s.claim_ref}</h1>
          <p className="text-sm text-slate-500 mt-1">{s.council} · {s.lga}</p>
        </div>
        <StatusPill status={s.status} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        <Stat label="AGRN" value={s.agrn} mono />
        <Stat label="Claim value" value={fmtAUD(s.value)} mono />
        <Stat label="RFIs raised" value={s.rfi_count} mono />
        <Stat label="Submission score" value={s.submission_score?.toFixed?.(1) ?? "—"} mono />
        <Stat label="Assessment score" value={s.assessment_score?.toFixed?.(1) ?? "—"} mono />
        <Stat label="Assessor" value={s.assessor_email || "Unassigned"} />
      </div>

      <div className="card p-5 mb-6">
        <h3 className="font-heading text-lg font-semibold mb-4">Assessment Wizard</h3>
        <Stepper steps={["LGA & AGRN","Eligibility","Scope & Works","Evidence","Decision"]} active={s.status === "Approved" ? 4 : s.status === "RFI Issued" ? 3 : 2} />
      </div>

      <div className="card p-5">
        <h3 className="font-heading text-lg font-semibold mb-4">Assessor decision</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label-upper mb-1 block">Status</label>
            <select className="input" value={statusDraft} onChange={e=>setStatusDraft(e.target.value)} data-testid="status-select">
              {["Draft","In Review","RFI Issued","Approved","Partially Approved","Ineligible"].map(x=><option key={x}>{x}</option>)}
            </select>
          </div>
          <div>
            <label className="label-upper mb-1 block">Assessor notes</label>
            <textarea className="input" rows={3} value={notesDraft} onChange={e=>setNotesDraft(e.target.value)} data-testid="notes-textarea" />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button className="btn-primary" onClick={save} disabled={saving} data-testid="save-decision-button">{saving ? "Saving…" : "Save decision"}</button>
          {sub && sub.portal_type && sub.portal_type.startsWith("EW") && (
            <button className="btn-secondary flex items-center gap-2" onClick={async()=>{
              const r = await fetch(`${API}/submissions/${sub.id}/ew-assessment-form`, {
                method:"POST",
                headers:{"Content-Type":"application/json"},
                body: JSON.stringify({defects:[{defect_id:"DI-001",asset_type:"Local road",location:"(complete in spreadsheet)",scope:sub.notes||"(complete in spreadsheet)",ex_gst:sub.value||0,outcome:sub.status==="Approved"?"Compliant":sub.status==="RFI Issued"?"Clarification required":"",recommendation:sub.status==="Approved"?"Recommend approve":sub.status==="RFI Issued"?"Recommend return for info":"",comments:sub.notes||""}],assessor:{name:user?.name||"",position:"RHE Assessor",date:new Date().toISOString().slice(0,10)}}),
              });
              const blob = await r.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a"); a.href = url; a.download = `${sub.claim_ref}_EW_Assessment.xlsx`; a.click();
            }} data-testid="generate-ew-form-button"><Upload className="w-4 h-4 rotate-180"/> Download EW Assessment Spreadsheet</button>
          )}
          {sub && sub.portal_type && sub.portal_type.startsWith("EPAR") && (
            <button className="btn-secondary flex items-center gap-2" onClick={async()=>{
              const today = new Date().toISOString().slice(0,10);
              const dcAll = ["D1","D2","D3","D4","D5","D6","C1","C2","C3","C4"];
              const sqpAll = ["S1","S2","S3","S4","S5","S6"];
              const r = await fetch(`${API}/submissions/${sub.id}/epar-assessment-form`, {
                method:"POST",
                headers:{"Content-Type":"application/json"},
                body: JSON.stringify({decision:{date_commenced:today, conflict_of_interest:"I DO NOT have a conflict of interest", confidentiality:"I agree", accuracy_integrity:"Yes", compliance_drfa:"Yes", sharepoint_link:"(paste SharePoint link here)", docs_filed:"Yes", file_loc_pre_disaster:"(SharePoint folder)", file_loc_damage:"(SharePoint folder)", file_loc_cost:"(SharePoint folder)", erc_supported_by_cet: sub.status==="Approved"?"Yes":"No", dc_ticks: sub.status==="Approved"?dcAll:[], sqp_ticks: sub.status==="Approved"?sqpAll:[], is_qs_engineer:"Yes", sqp_name:user?.name||"", sqp_position:"RHE Assessor — SQP", date_certified: today }}),
              });
              const blob = await r.blob();
              const isDraft = r.headers.get("x-validation-errors") && r.headers.get("x-validation-errors")!=="0";
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a"); a.href = url; a.download = `${sub.claim_ref}_EPAR_SQP_Assessment${isDraft?"_DRAFT":""}.xlsx`; a.click();
            }} data-testid="generate-epar-form-button"><Upload className="w-4 h-4 rotate-180"/> Download EPAR SQP Assessment Record</button>
          )}
        </div>
      </div>
    </div>
  );
}

function Stepper({ steps, active }) {
  return (
    <div className="flex items-center gap-2" data-testid="wizard-stepper">
      {steps.map((s, i) => {
        const done = i < active, cur = i === active;
        return (
          <div key={s} className="flex items-center gap-2 flex-1">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${done? "bg-[#002664] text-white" : cur? "bg-[#002664] text-white ring-4 ring-blue-100": "bg-slate-100 text-slate-500"}`}>
              {done ? <CheckCircle2 className="w-4 h-4"/> : i+1}
            </div>
            <div className={`text-xs ${cur?"text-slate-900 font-semibold":"text-slate-500"}`}>{s}</div>
            {i < steps.length-1 && <div className="flex-1 h-px bg-slate-200" />}
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Photo Checker (standalone) ---------- */
/* ---------- Ingest Inbox (drop ZIP / paste Drive link) ---------- */
/* ---------- Single Ingest Job — sort needs_review ---------- */
/* fmtMoney imported from lib/format */

/* ---------- CET Verifier (Scope assessments) ---------- */
/* ---------- Expert Chat ---------- */
/* ---------- Knowledge Base ---------- */
/* ---------- App ---------- */


function App() {
  const { user, save, clear } = useAuth();
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" /> : <Login onLogin={save} />} />
        {/* Public marketing showcase — no Shell, no auth wall. */}
        <Route path="/showcase" element={<Showcase />} />
        {/* Standalone tools + workflow walkthroughs — public, no login wall. */}
        <Route path="/tools/ledger-checker" element={<Shell user={user} onLogout={() => clear()}><LedgerChecker /></Shell>} />
        <Route path="/tools/photo-checker" element={<Shell user={user} onLogout={() => clear()}><PhotoChecker /></Shell>} />
        <Route path="/tools/cet-verifier" element={<Shell user={user} onLogout={() => clear()}><CETVerifier /></Shell>} />
        <Route path="/workflow/epar-scope" element={<Shell user={user} onLogout={() => clear()}><WorkflowEPARScope /></Shell>} />
        <Route path="/workflow/ew-finalisation" element={<Shell user={user} onLogout={() => clear()}><WorkflowEWFinalisation /></Shell>} />
        <Route path="/workflow/epar-finalisation" element={<Shell user={user} onLogout={() => clear()}><WorkflowEPARFinalisation /></Shell>} />
        <Route path="/security" element={<Shell user={user} onLogout={() => clear()}><SecurityStatement /></Shell>} />
        <Route path="/precedents" element={<Shell user={user} onLogout={() => clear()}><Precedents /></Shell>} />
        {/* Inbox + submission + portal views are public read-only for guests; logged-in assessors get write actions inside the components themselves. */}
        <Route path="/inbox" element={<Shell user={user} onLogout={() => clear()}><IngestInbox /></Shell>} />
        <Route path="/inbox/:jobId" element={<Shell user={user} onLogout={() => clear()}><IngestJob /></Shell>} />
        <Route path="/submission/:id" element={<Shell user={user} onLogout={() => clear()}><SubmissionDetail /></Shell>} />
        <Route path="/portal/:portal_type" element={<Shell user={user} onLogout={() => clear()}><PortalPage /></Shell>} />
        <Route path="/knowledge-base" element={<Shell user={user} onLogout={() => clear()}><KnowledgeBase /></Shell>} />
        <Route path="/*" element={
          !user ? <Navigate to="/login" /> :
          <Shell user={user} onLogout={() => { clear(); }}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/ai-assistant" element={<AIAssistant />} />
            </Routes>
          </Shell>
        }/>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
